import { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';

/**
 * DateRangePicker — selector de rango de fechas con popover.
 *
 * Props:
 *   from: string | undefined  — fecha inicio "YYYY-MM-DD"
 *   to:   string | undefined  — fecha fin "YYYY-MM-DD"
 *   onChange: ({ from, to }) => void
 */
export function DateRangePicker({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fromDate = from ? new Date(from + 'T00:00:00') : undefined;
  const toDate   = to   ? new Date(to   + 'T00:00:00') : undefined;

  function handleSelect(range) {
    onChange({
      from: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
      to:   range?.to   ? format(range.to,   'yyyy-MM-dd') : undefined,
    });
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange({ from: undefined, to: undefined });
  }

  function label() {
    if (fromDate && toDate) return `${format(fromDate, 'd MMM', { locale: es })} – ${format(toDate, 'd MMM yyyy', { locale: es })}`;
    if (fromDate) return `Desde ${format(fromDate, 'd MMM yyyy', { locale: es })}`;
    return <span className="text-gray-400">Seleccionar rango...</span>;
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] bg-white focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] whitespace-nowrap"
      >
        <span className="text-[#6B7280]">📅</span>
        <span className={fromDate ? 'text-[#0D2B5C]' : ''}>{label()}</span>
        {(from || to) && (
          <span
            onClick={handleClear}
            className="ml-1 text-gray-400 hover:text-gray-600 leading-none"
          >
            ✕
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <DayPicker
            mode="range"
            selected={{ from: fromDate, to: toDate }}
            onSelect={handleSelect}
            locale={es}
            showOutsideDays
            classNames={{
              root: "font-['Inter'] text-sm",
              months: 'flex gap-4',
              month_caption: "font-['Raleway'] font-semibold text-[#0D2B5C] text-center pb-2 capitalize",
              nav: 'flex items-center justify-between mb-1',
              button_previous: 'text-[#6B7280] hover:text-[#0D2B5C] px-1 transition-colors',
              button_next: 'text-[#6B7280] hover:text-[#0D2B5C] px-1 transition-colors',
              weekdays: 'flex',
              weekday: 'w-9 text-center text-[#6B7280] text-xs pb-1 capitalize',
              weeks: 'flex flex-col gap-0.5',
              week: 'flex',
              day: 'w-9 h-9 flex items-center justify-center rounded-md text-[#0D2B5C] hover:bg-gray-100 cursor-pointer transition-colors text-sm',
              day_button: 'w-full h-full flex items-center justify-center',
              selected: 'bg-[#0D2B5C] text-white hover:bg-[#0D2B5C]',
              range_start: 'bg-[#0D2B5C] text-white rounded-l-md',
              range_end: 'bg-[#0D2B5C] text-white rounded-r-md',
              range_middle: 'bg-[#0D2B5C]/10 text-[#0D2B5C] rounded-none',
              today: 'font-bold text-[#1E9BE8]',
              outside: 'text-gray-300',
              disabled: 'opacity-30 cursor-not-allowed',
            }}
          />
        </div>
      )}
    </div>
  );
}
